import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";

const blank = () => ({ staff_id: "", role: "", hours: "", rate: "", deductions: "0" });
const num = (v) => Number(v) || 0;

/** Payroll line items: editable while draft (SA), read-only once approved. */
export default function PayrollLines({ rec, reload }) {
  const isSuper = useIsSuper();
  const editable = isSuper && rec.status === "draft";
  const [rows, setRows] = useState(() => rec.line_items.map((li) => ({ ...li, hours: String(li.hours), rate: String(li.rate), deductions: String(li.deductions) })));
  const [payDate, setPayDate] = useState(rec.pay_date);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (editable) finApi.get("/session-payouts/staff").then((d) => setStaff(d.staff)).catch(() => {}); }, [editable]);
  const set = (i, k) => (e) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));
  const save = async () => {
    setBusy(true);
    try {
      await finApi.patch(`/payroll/${rec.id}`, { pay_date: payDate, line_items: rows.map((r) => ({ staff_id: r.staff_id, role: r.role, hours: num(r.hours), rate: num(r.rate), deductions: num(r.deductions) })) });
      toast.success("Payroll run saved");
      reload();
    } catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  const gross = rows.reduce((a, r) => a + num(r.hours) * num(r.rate), 0);
  const net = rows.reduce((a, r) => a + num(r.hours) * num(r.rate) - num(r.deductions), 0);

  return (
    <div data-testid="fin-pr-lines" className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Line items {editable ? "· editable while draft" : "· locked"}</span>
        <label className="ml-auto flex items-center gap-2 text-xs text-nex-muted">Pay date
          <input type="date" value={payDate} disabled={!editable} onChange={(e) => setPayDate(e.target.value)} data-testid="fin-pr-paydate" className={`${field} !py-1`} /></label>
      </div>
      <div className="border border-white/10 rounded-sm overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
            {["Staff", "Role", "Hours", "Rate", "Gross", "Deductions", "Net", ""].map((h) => <th key={h} className="px-2 py-2 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const g = num(r.hours) * num(r.rate);
              return (
                <tr key={i} data-testid={`fin-pr-line-${i}`} className="border-t border-white/5">
                  <td className="px-2 py-1.5">{editable ? (
                    <select value={r.staff_id} onChange={set(i, "staff_id")} data-testid={`fin-pr-line-${i}-staff`} className={`${field} !py-1 w-40`}>
                      <option value="">Select…</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
                    </select>) : <span className="text-nex-silver">{r.staff_name}</span>}</td>
                  {["role", "hours", "rate"].map((k) => (
                    <td key={k} className="px-2 py-1.5">{editable ? <input value={r[k]} onChange={set(i, k)} type={k === "role" ? "text" : "number"} min="0" step="0.01" data-testid={`fin-pr-line-${i}-${k}`} className={`${field} !py-1 ${k === "role" ? "w-28" : "w-20"}`} /> : <span className="text-nex-muted">{k === "rate" ? money(r[k]) : r[k]}</span>}</td>
                  ))}
                  <td className="px-2 py-1.5 font-mono text-xs text-nex-silver">{money(g)}</td>
                  <td className="px-2 py-1.5">{editable ? <input value={r.deductions} onChange={set(i, "deductions")} type="number" min="0" step="0.01" data-testid={`fin-pr-line-${i}-deductions`} className={`${field} !py-1 w-20`} /> : <span className="text-nex-muted">{money(r.deductions)}</span>}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-nex-gold">{money(g - num(r.deductions))}</td>
                  <td className="px-2 py-1.5">{editable && <button type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} data-testid={`fin-pr-line-${i}-remove`} aria-label="Remove line" className="text-nex-muted hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                </tr>
              );
            })}
            <tr className="border-t border-nex-gold/30 font-mono text-xs">
              <td colSpan={4} className="px-2 py-2 text-right text-nex-muted uppercase tracking-[0.2em] text-[10px]">Totals</td>
              <td data-testid="fin-pr-total-gross" className="px-2 py-2 text-nex-silver">{money(gross)}</td><td />
              <td data-testid="fin-pr-total-net" className="px-2 py-2 text-nex-gold">{money(net)}</td><td />
            </tr>
          </tbody>
        </table>
      </div>
      {editable && (
        <div className="flex gap-2">
          <button type="button" onClick={() => setRows((rs) => [...rs, blank()])} data-testid="fin-pr-add-line" className={`${btnGold} inline-flex items-center gap-1`}><Plus className="h-3 w-3" /> Add line</button>
          <button type="button" disabled={busy || rows.some((r) => !r.staff_id)} onClick={save} data-testid="fin-pr-save" className={`${btnGold} ml-auto`}>Save run</button>
        </div>
      )}
    </div>
  );
}
